import { OnApproveActions, OnApproveData } from '@paypal/paypal-js/types/components/buttons';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { exposeGlobally } from '@utils/utils';

type PayPalButtonContextData = {
  onApprove: (data: OnApproveData, actions: OnApproveActions) => Promise<void>;
  onCancel?: () => void;
  isButtonDisabled: boolean;
  userIdToken?: string;
};

const PayPalButtonContext = createContext<PayPalButtonContextData>({
  onApprove: async () => {},
  onCancel: () => {},
  isButtonDisabled: false,
});

export const usePayPalButtonContext = () => useContext(PayPalButtonContext);

type PayPalButtonContextProviderProps = {
  onApprove: (data: OnApproveData, actions: OnApproveActions) => Promise<void>;
  onCancel?: () => void;
  userIdToken?: string;
  children: ReactNode;
};

const PayPalButtonContextProvider = ({
  onApprove,
  onCancel,
  userIdToken,
  children,
}: PayPalButtonContextProviderProps) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const disablePayPalButton = () => {
    setIsButtonDisabled(true);
  };

  const enablePayPalButton = () => {
    setIsButtonDisabled(false);
  };

  useEffect(() => {
    // Expose the two functions globally, so that "shop-checkout" will be
    // able to toggle the PayPal buttons from their side.
    exposeGlobally('disablePayPalButton', disablePayPalButton);
    exposeGlobally('enablePayPalButton', enablePayPalButton);
  }, []);

  // Memo the provider value object to prevent unwanted render of the Provider
  const providerValue = useMemo(() => {
    return {
      onApprove,
      onCancel,
      isButtonDisabled,
      userIdToken,
    };
  }, [onApprove, onCancel, isButtonDisabled, userIdToken]);

  return (
    <PayPalButtonContext.Provider value={providerValue}>{children}</PayPalButtonContext.Provider>
  );
};

export default PayPalButtonContextProvider;
